// assets/predictive-search.js
(() => {
  const Base = window.SearchForm || HTMLElement;

  if (!customElements.get('predictive-search')) {
    class PredictiveSearch extends Base {
      constructor() {
        super();

        // ===== base wiring (ทำให้ทำงานได้แม้ไม่มี SearchForm) =====
        this.input =
          this.querySelector('input[type="search"], input[name="q"]') ||
          this.querySelector('input');

        // state
        this.cachedResults = {};
        this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
        this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
        this.isOpen = false;
        this.abortController = new AbortController();
        this.searchTerm = '';

        this.setupEventListeners();
      }

      /* ========== events ========== */
      setupEventListeners() {
        if (!this.input) return;

        // รองรับทั้งกรณีมี/ไม่มี Base(SearchForm)
        this.input.addEventListener('input', this.onChange.bind(this));

        if (this.input.form) this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
        this.input.form?.addEventListener('reset', this.onFormReset.bind(this));

        this.input.addEventListener('focus', this.onFocus.bind(this));
        this.addEventListener('focusout', this.onFocusOut.bind(this));
        this.addEventListener('keyup', this.onKeyup.bind(this));
        this.addEventListener('keydown', this.onKeydown.bind(this));
      }

      getQuery() {
        return (this.input?.value || '').trim();
      }

      onChange() {
        // ถ้ามีเมธอดใน Base ค่อยเรียก (หลีกเลี่ยง error super.*)
        try {
          if (Base !== HTMLElement && typeof super.onChange === 'function') super.onChange();
        } catch (_) {}

        const newSearchTerm = this.getQuery();

        // ล้างผลเก่าเมื่อพิมพ์ย้อน/เปลี่ยนรากของคำค้น
        if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
          this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
        }

        // อัปเดตปุ่ม "ค้นหา …" ให้ไว ไม่ต้องรอ fetch
        this.updateSearchForTerm(this.searchTerm, newSearchTerm);

        this.searchTerm = newSearchTerm;

        if (!this.searchTerm.length) {
          this.close(true);
          return;
        }

        this.getSearchResults(this.searchTerm);
      }

      onFormSubmit(event) {
        if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
          event.preventDefault();
        }
      }

      onFormReset(event) {
        // ถ้ามี Base และนิยาม shouldResetForm ให้ใช้ logic เดิม มิฉะนั้นรีเซ็ตตรงๆ
        let should = true;
        try {
          if (Base !== HTMLElement && typeof super.onFormReset === 'function') super.onFormReset(event);
          if (Base !== HTMLElement && typeof super.shouldResetForm === 'function') {
            should = !!super.shouldResetForm();
          }
        } catch (_) {}

        if (should) {
          this.searchTerm = '';
          try { this.abortController.abort(); } catch (_) {}
          this.abortController = new AbortController();
          this.closeResults(true);
        }
      }

      onFocus() {
        const currentSearchTerm = this.getQuery();
        if (!currentSearchTerm.length) return;

        if (this.searchTerm !== currentSearchTerm) {
          // คำค้นเปลี่ยนจาก input อื่น
          this.onChange();
        } else if (this.getAttribute('results') === 'true') {
          this.open();
        } else {
          this.getSearchResults(this.searchTerm);
        }
      }

      onFocusOut() {
        setTimeout(() => {
          if (!this.contains(document.activeElement)) this.close();
        });
      }

      onKeyup(event) {
        if (!this.getQuery().length) this.close(true);
        // กันเบราว์เซอร์บางตัวยิง keyup แล้ว scroll/submit
        if (['ArrowUp', 'ArrowDown', 'Enter'].includes(event.code)) event.preventDefault();

        switch (event.code) {
          case 'ArrowUp':   this.switchOption('up');   break;
          case 'ArrowDown': this.switchOption('down'); break;
          case 'Enter':     this.selectOption();       break;
        }
      }

      onKeydown(event) {
        // กัน caret ขยับตอนกดขึ้น/ลง
        if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
      }

      /* ========== helpers ========== */
      updateSearchForTerm(previousTerm, newTerm) {
        if (!previousTerm) return;

        const el = this.querySelector('[data-predictive-search-search-for-text]');
        const current = el?.innerText;
        if (!current) return;

        const safePrev = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const matches = current.match(new RegExp(safePrev, 'g')) || [];
        if (matches.length > 1) return; // ป้องกันแทนที่ส่วนอื่นของข้อความ

        el.innerText = current.replace(new RegExp(safePrev), newTerm);
      }

      switchOption(direction) {
        if (!this.getAttribute('open')) return;

        const moveUp = direction === 'up';
        const selectedElement = this.querySelector('[aria-selected="true"]');

        const allVisibleElements = Array.from(
          this.querySelectorAll('li, button.predictive-search__item')
        ).filter((el) => el.offsetParent !== null);

        if (moveUp && !selectedElement) return;

        let selectedIndex = -1;
        allVisibleElements.some((el, i) => (selectedIndex = el === selectedElement ? i : selectedIndex) > -1);

        if (this.statusElement) this.statusElement.textContent = '';

        let activeIndex = 0;
        if (!moveUp && selectedElement) {
          activeIndex = selectedIndex === allVisibleElements.length - 1 ? 0 : selectedIndex + 1;
        } else if (moveUp) {
          activeIndex = selectedIndex === 0 ? allVisibleElements.length - 1 : selectedIndex - 1;
        }

        if (activeIndex === selectedIndex) return;

        const active = allVisibleElements[activeIndex];
        active.setAttribute('aria-selected', 'true');
        selectedElement?.setAttribute('aria-selected', 'false');
        this.input.setAttribute('aria-activedescendant', active.id);
      }

      selectOption() {
        const selected = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
        if (selected) selected.click();
      }

      /* ========== data flow ========== */
      getSearchResults(searchTerm) {
        const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
        this.setLiveRegionLoadingState();

        // ยกเลิกคำขอเดิมเสมอเพื่อกันผลย้อนกลับ
        try { this.abortController.abort(); } catch (_) {}
        this.abortController = new AbortController();

        if (this.cachedResults[queryKey]) {
          this.renderSearchResults(this.cachedResults[queryKey]);
          return;
        }

        fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
          signal: this.abortController.signal,
        })
          .then((res) => {
            if (!res.ok) { this.close(); throw new Error(res.status); }
            return res.text();
          })
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const host =
              doc.querySelector('#shopify-section-predictive-search') ||
              doc.querySelector('[data-predictive-search]') ||
              doc.body;
            const resultsMarkup = host.innerHTML;

            // sync cache ข้าม instances
            this.allPredictiveSearchInstances.forEach((ps) => (ps.cachedResults[queryKey] = resultsMarkup));
            this.renderSearchResults(resultsMarkup);
          })
          .catch((error) => {
            if (error?.code === 20) return; // ถูก abort
            this.close();
          });
      }

      setLiveRegionLoadingState() {
        this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
        this.loadingText = this.loadingText || this.getAttribute('data-loading-text') || 'Loading…';
        if (this.statusElement) this.setLiveRegionText(this.loadingText);
        this.setAttribute('loading', 'true');
      }

      setLiveRegionText(statusText) {
        if (!this.statusElement) return;
        this.statusElement.setAttribute('aria-hidden', 'false');
        this.statusElement.textContent = statusText;
        setTimeout(() => this.statusElement.setAttribute('aria-hidden', 'true'), 1000);
      }

      renderSearchResults(resultsMarkup) {
        if (!this.predictiveSearchResults) return;
        this.predictiveSearchResults.innerHTML = resultsMarkup;
        this.setAttribute('results', 'true');
        this.setLiveRegionResults();
        this.open();
      }

      setLiveRegionResults() {
        this.removeAttribute('loading');
        const num = this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || '';
        if (num) this.setLiveRegionText(num);
      }

      getResultsMaxHeight() {
        const hdr = document.querySelector('.section-header, .shopify-section-header');
        const bottom = hdr ? hdr.getBoundingClientRect().bottom : 0;
        this.resultsMaxHeight = Math.max(160, window.innerHeight - bottom);
        return this.resultsMaxHeight;
      }

      /* ========== open/close ========== */
      open() {
        if (this.predictiveSearchResults) {
          this.predictiveSearchResults.style.maxHeight =
            this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
        }
        this.setAttribute('open', 'true');
        this.input?.setAttribute('aria-expanded', 'true');
        this.isOpen = true;
      }

      close(clearSearchTerm = false) {
        this.closeResults(clearSearchTerm);
        this.isOpen = false;
      }

      closeResults(clearSearchTerm = false) {
        if (clearSearchTerm && this.input) {
          this.input.value = '';
          this.removeAttribute('results');
        }
        const selected = this.querySelector('[aria-selected="true"]');
        selected?.setAttribute('aria-selected', 'false');

        this.input?.setAttribute('aria-activedescendant', '');
        this.removeAttribute('loading');
        this.removeAttribute('open');
        this.input?.setAttribute('aria-expanded', 'false');
        this.resultsMaxHeight = false;
        this.predictiveSearchResults?.removeAttribute('style');
      }
    }

    customElements.define('predictive-search', PredictiveSearch);
  }
})();
